import React from 'react';
import RuleTypeSelect from './RuleTypeSelect';
import FeedbackRuleWizard from './FeedbackRuleWizard';
import CallRuleWizard from './CallRuleWizard';
import AuthStore from 'stores/AuthStore';
import TriggerRuleActions from 'actions/triggers/TriggerRuleActions';
import FeedbackTrigger, {
  FeedbackMatchAllContext,
  FeedbackMatchAnyContext,
  FeedbackRecipientContext,
  FeedbackTriggerCommentContext,
} from 'records/FeedbackTrigger';
import CallTrigger, {
  CallConditionAll,
  CallConditionAny,
  CallConditionType,
  CallConditionUser,
  SpeakerCondition,
} from 'records/CallTrigger';
import {browserHistory, withRouter} from 'react-router';
import {unsavedMessage} from 'utils/const';
import './CreateTriggerRule.styl';
import {UserConditionType} from 'records/UserCondition';
import FilterWordsActions from 'actions/triggers/FilterWordsActions';
import FilterWordsStore from 'stores/triggers/FilterWordsStore';

class CreateTriggerRule extends React.Component {
  constructor(props) {
    super(props);

    this.selectRuleType = this.selectRuleType.bind(this);
    this.cancelClick = this.cancelClick.bind(this);
    this.cancelAllClick = this.cancelAllClick.bind(this);
    this.saveTriggerClick = this.saveTriggerClick.bind(this);
    this.routerWillLeave = this.routerWillLeave.bind(this);
    this.createTemplate = this.createTemplate.bind(this);

    this.state = {
      rule: {},
      confirmLeave: true,
    };
  }

  selectRuleType({triggerType}) {
    // if the type changes, blow away the rule and start over
    const rule =
      triggerType === 'call_trigger'
        ? new CallTrigger()
        : new FeedbackTrigger({
            user: [UserConditionType.getUserCondition(UserConditionType.ANY_USER)],
            context: new FeedbackMatchAllContext(),
          });

    this.setState({
      rule,
    });
  }

  saveTriggerClick(rule) {
    const {cancelNav} = this.state;
    this.setState({confirmLeave: false});

    TriggerRuleActions.createTriggerRule(rule).then(rule => {
      // initial create is set - so when canceling later, we know
      // that we should delete the rule created so far and go back to the list
      browserHistory.push({
        pathname: `/admin/actions/${rule.id}/edit-details`,
        state: {initialCreate: true, cancelNav},
      });
    });
  }

  /**
   * Clear the rule. If state dictates, go back to some other page. Otherwise, the editor
   * starts over with a fresh rule.
   */
  cancelClick() {
    const {cancelNav} = this.state;
    this.setState({rule: {}});
    if (cancelNav) {
      cancelNav();
    }
  }

  /**
   * go back to the rules list
   */
  cancelAllClick() {
    this.setState({confirmLeave: false});
    browserHistory.goBack();
  }

  routerWillLeave() {
    if (this.state.rule.type && this.state.confirmLeave) {
      return unsavedMessage;
    }
  }

  defaultFeedbackTrigger(authUser) {
    const context = new FeedbackMatchAllContext({
      matchAll: [
        new FeedbackMatchAllContext({
          matchAll: [
            new FeedbackTriggerCommentContext(),
            new FeedbackMatchAnyContext({
              matchAny: [
                new FeedbackRecipientContext({
                  recipient: UserConditionType.getUserCondition(
                    UserConditionType.USER,
                    authUser.id
                  ),
                }),
              ],
            }),
          ],
        }),
      ],
    });
    return new FeedbackTrigger({
      context: context,
      user: [UserConditionType.getUserCondition(UserConditionType.TEAM, authUser.teamId)],
    });
  }

  defaultCallTrigger(authUser) {
    const filterWords = FilterWordsStore.getState().filterWords;
    const topic = filterWords.find(w => w.word.toLowerCase() === '(buyer) negative words');
    const conditions = [
      new CallConditionAny({
        matchAny: [
          new CallConditionUser({
            userCondition: UserConditionType.getUserCondition(
              UserConditionType.TEAM,
              authUser.teamId
            ),
          }),
        ],
      }),
      new CallConditionAny({
        matchAny: [
          CallConditionType.getCallCondition({
            type: CallConditionType.CONVERSATION_LENGTH,
            timeRange: {start: 600, end: 7200},
          }),
        ],
      }),
      new CallConditionAny({
        matchAny: [
          CallConditionType.getCallCondition({
            type: CallConditionType.TOPIC_COVERAGE,
            speakerType: SpeakerCondition.EXTERNAL,
            keywordTopicPhrase: {filterWordId: topic && topic.id},
            range: {start: 20, end: 100},
          }),
        ],
      }),
      new CallConditionAny({
        matchAny: [
          CallConditionType.getCallCondition({
            type: CallConditionType.MENTIONED_AT,
            speakerType: SpeakerCondition.EXTERNAL,
            keywordTopicPhrase: {filterWordId: topic && topic.id},
            range: {start: 80, end: 100},
          }),
        ],
      }),
    ];
    return new CallTrigger({
      conditions: new CallConditionAll({matchAll: conditions}),
    });
  }

  componentDidMount() {
    this.props.router.setRouteLeaveHook(this.props.route, this.routerWillLeave);
    FilterWordsActions.fetchAll();
    const {location} = this.props;
    if (location.state) {
      this.setState(location.state);
      browserHistory.replace({
        pathname: location.pathname,
        state: undefined,
      });
    }
  }

  createTemplate(ruleType) {
    const authUser = AuthStore.getState().user;
    if (ruleType === 'feedback_trigger') {
      this.setState({
        rule: this.defaultFeedbackTrigger(authUser),
      });
    } else if (ruleType === 'call_trigger') {
      this.setState({
        rule: this.defaultCallTrigger(authUser),
      });
    }
  }

  renderSelectedRuleType(rule) {
    if (rule.type === 'feedback_trigger') {
      return (
        <FeedbackRuleWizard
          rule={rule}
          cancelClick={this.cancelClick}
          saveTriggerClick={this.saveTriggerClick}
        />
      );
    } else if (rule.type === 'call_trigger') {
      return (
        <CallRuleWizard
          rule={rule}
          cancelClick={this.cancelClick}
          saveTriggerClick={this.saveTriggerClick}
        />
      );
    } else {
      return (
        <RuleTypeSelect
          selectType={this.selectRuleType}
          cancelClick={this.cancelAllClick}
          templateClick={this.createTemplate}
        />
      );
    }
  }

  render() {
    const {rule} = this.state;
    return (
      <div className="triggers-page create-trigger-rule">
        <header>
          <h1 style={{margin: 0}}>New Smart Alert</h1>
        </header>
        {this.renderSelectedRuleType(rule)}
      </div>
    );
  }
}

export default withRouter(CreateTriggerRule);
